import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Button } from '@material-ui/core';
import { darkGrey, universalStyles } from '../../../../app/theme';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import ExternalLinkModal from '../../../micro/ExternalLinkModal/ExternalLinkModal';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  heading: {
    marginTop: 46,
  },
  diagram: {
    width: '80%',
    marginLeft: '10%',
  },
  externalLink: {
    color: darkGrey,
    textTransform: 'none',
    textDecoration: 'underline',
    padding: 0,
    fontSize: 18,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  link: {
    color: darkGrey,
    fontSize: 18,
    fontWeight: 600,
    textDecoration: 'underline',
  },
}));

const InterpretationPage02 = (): JSX.Element => {
  const classes = useStyles();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalUrl, setModalUrl] = useState<string>('');

  const handleClickFDAMedWatch = () => {
    setModalUrl('https://www.fda.gov/medwatch');
    setIsModalOpen(true);
  };

  return (
    <>
      <div className={classes.grayBox}>
        <div className={classes.whiteBox}>
          <Typography variant="h4">Indication</Typography>
          <Typography variant="body1">
            Axumin
            <sup>®</sup>
            &nbsp;(fluciclovine F 18) injection is indicated for positron emission tomography (PET) imaging in men with
            suspected prostate cancer recurrence based on elevated blood prostate specific antigen (PSA) levels
            following prior treatment.
          </Typography>
          <img
            className={classes.diagram}
            src={`${BASE_URL}${IMAGE_PATHS.TRAINING_PAGES[1].INDICATION.path}`}
            alt={`${IMAGE_PATHS.TRAINING_PAGES[1].INDICATION.alt}`}
          />
          <Typography variant="h4">Important Safety Information</Typography>
          <ul className={classes.list}>
            <li>
              Image interpretation errors can occur with Axumin PET imaging. A negative image does not rule out
              recurrent prostate cancer and a positive image does not confirm its presence. The performance of Axumin
              seems to be affected by PSA levels. Axumin uptake may occur with other cancers and benign prostatic
              hypertrophy in primary prostate cancer. Clinical correlation, which may include histopathological
              evaluation, is recommended.
            </li>
            <li>
              Hypersensitivity reactions, including anaphylaxis, may occur in patients who receive Axumin. Emergency
              resuscitation equipment and personnel should be immediately available.
            </li>
            <li>
              Axumin use contributes to a patient’s overall long-term cumulative radiation exposure, which is associated
              with an increased risk of cancer. Safe handling practices should be used to minimize radiation exposure to
              the patient and health care providers.
            </li>
            <li>
              Adverse reactions were reported in ≤1% of subjects during clinical studies with Axumin. The most common
              adverse reactions were injection site pain, injection site erythema and dysgeusia.
            </li>
          </ul>
          <Typography variant="body1">
            To report suspected adverse reactions to Axumin, call
            <strong> 1-855-AXUMIN1 (1-855-298-6461) </strong>
            or contact FDA at
            <strong> 1-800-FDA-1088 </strong>
            or&nbsp;
            <Button className={classes.externalLink} onClick={handleClickFDAMedWatch}>
              www.fda.gov/medwatch
            </Button>
            .
          </Typography>
          <Typography variant="body1">
            Please see Axumin&nbsp;
            <a
              className={classes.link}
              target="_blank"
              rel="noreferrer"
              href="https://www.axumin.com/prescribing-information.pdf"
            >
              full Prescribing Information
            </a>
            .
          </Typography>
          <Typography variant="h4" className={classes.heading}>
            Important Imaging Considerations
          </Typography>
          <ul className={classes.list}>
            <li>
              Axumin training was developed as a resource to help familiarize you with techniques for the safe and
              effective usage of Axumin.
            </li>
            <li>
              Images should only be interpreted by readers trained in the interpretation of PET images with Axumin.
            </li>
            <li>
              The responsibility for the accurate and timely acquisition and interpretation of images using Axumin
              scanning rests with the nuclear medicine physician or radiologist supervising the PET imaging facility.
            </li>
            <li>
              Axumin training is not intended to substitute for independent medical judgment of the physician(s)
              responsible for the individual patient’s management, nor is it a guarantee of any specific clinical
              results.
            </li>
          </ul>
          <ExternalLinkModal
            url={modalUrl}
            isOpen={isModalOpen}
            handleModalOpen={(isOpen: boolean) => setIsModalOpen(isOpen)}
          />
        </div>
      </div>
    </>
  );
};

export default InterpretationPage02;
